import { rewardService } from '@theme/reward-service';

/**
 * <includ-pdp-cash> — the Includ Cash card on the product page.
 *
 * Paints two numbers into blocks/includ-pdp-rewards.liquid:
 *   - the shopper's BALANCE, from the Includ backend via rewardService
 *   - what this product EARNS, recomputed when the selected variant changes
 *
 * V2 of the `.includ-coins` block that used to live inside
 * snippets/spectrum-coupons-drawer.liquid. That one called the Spectrum app
 * proxy (`/apps/spectrum_ai_includ/rewards/balance`) and read `data.active`;
 * everything here goes through the same `rewardService` singleton the cart uses,
 * so the PDP and the cart can no longer disagree about a shopper's balance, the
 * earn rate, or whether the programme is switched on at all.
 *
 * Split of responsibilities, same as every other Includ Cash surface:
 *   - LIQUID owns every string, the layout, the first-paint earn amount and the
 *     whole guest state.
 *   - JS (here) writes two numbers. It never builds markup.
 *
 * Addressed by `data-` attribute rather than `ref`, matching
 * assets/reward-service.js: refs are refreshed by a MutationObserver and are
 * stale in the synchronous window right after a section morph.
 */

/**
 * Selected-variant id, read from the same place the buy form reads it, so the
 * earn line can never describe a different variant than the one that would be
 * added to the bag.
 * @returns {number} the variant id, or 0 when the form hasn't rendered one
 */
function selectedVariantId() {
  const input = document.querySelector('[name="id"]');
  return input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
}

/**
 * The quantity about to be bought.
 *
 * Today the PDP's quantity is a hidden input pinned to 1 (blocks/pdp-buy-actions.liquid),
 * so this always returns 1 — it is read rather than assumed so that adding a
 * quantity picker later does not silently leave the earn line describing one unit.
 * @returns {number} at least 1
 */
function selectedQuantity() {
  const input = document.querySelector('[name="quantity"]');
  const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : NaN;
  return Number.isFinite(value) && value > 0 ? value : 1;
}

class IncludPdpCash extends HTMLElement {
  /** @type {Record<string, number>} variant id → unit price in paise */
  #prices = {};

  /** @type {(event: Event) => void} */
  #onVariantChange = () => {};

  connectedCallback() {
    // Includ Cash is off for this platform — the card is already hidden by CSS
    // (`.cart-v2-rewards-off`, set on <html> in layout/theme.liquid from the two
    // theme settings). Bailing here is what stops the backend call: a hidden card
    // must not cost a proxy round-trip.
    if (!rewardService.enabled) return;

    this.#prices = this.#readPrices();

    // Liquid already rendered the earn amount for the variant this page was built
    // with, so the first paint is correct without JS and this only has to keep up
    // with changes. blocks/custom-variant-selector.liquid dispatches
    // `variant:change` on `document`; its detail carries the id but NOT the price,
    // which is why the price map above exists.
    this.#onVariantChange = () => this.#paintEarn();
    document.addEventListener('variant:change', this.#onVariantChange);

    this.#paintBalance();
    this.#paintRefer();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.#onVariantChange);
  }

  /**
   * Fetches the balance and writes it into the card.
   *
   * Guests are resolved entirely in Liquid — the card offers a sign-in instead of
   * a number — so `data-customer` gates this the same way `data-rewards-customer`
   * gates the cart's card. Without that gate a guest would spend a round-trip on a
   * balance that is always 0, and the paint would overwrite the guest copy with
   * "₹0 Includ Cash available".
   * @returns {Promise<void> | undefined}
   */
  #paintBalance() {
    if (!this.hasAttribute('data-customer')) return;

    const slot = this.querySelector('[data-cash-balance]');
    if (!slot) return;

    // Resolves 0 rather than rejecting — same contract the cart's card relies on,
    // so a failed request reads as an empty wallet rather than an error in the
    // shopper's face. Deliberately the same trade-off the cart makes: both
    // surfaces now say the same thing about the same failure.
    return rewardService.getBalance().then((balance) => {
      slot.innerHTML = '';

      const amount = document.createElement('strong');
      amount.textContent = rewardService.format(balance);

      slot.append(amount, ' INCLUD Cash available');
    });
  }

  /**
   * Shows the "Refer a friend" line to signed-in app shoppers who have never
   * referred. The read is assets/includ-sheets.js `referred` — the refer sheet's,
   * at most once a session — so the two never disagree. That script is deferred
   * and may run after this module, hence the wait.
   */
  #paintRefer() {
    const link = this.querySelector('[data-cash-refer]');
    if (!link || !this.hasAttribute('data-customer') || !window.includIsApp) return;

    const run = () =>
      window.IncludSheets?.referred().then((yes) => {
        link.hidden = yes !== false;
      });

    if (window.IncludSheets) run();
    else document.addEventListener('DOMContentLoaded', run, { once: true });
  }

  /** Recomputes the earn line for the currently selected variant. */
  #paintEarn() {
    const slot = this.querySelector('[data-cash-earn]');
    if (!slot) return;

    const unitPrice = this.#prices[String(selectedVariantId())];
    if (!Number.isFinite(unitPrice)) return; // unknown variant — leave the last good number

    // rewardService owns the 5% rule. The Liquid that rendered the first paint
    // applies the same arithmetic; see the comment above it in the block.
    slot.textContent = String(rewardService.earned(unitPrice * selectedQuantity()));
  }

  /**
   * The variant-id → unit-price map the block emits as JSON.
   * @returns {Record<string, number>} empty when the payload is missing or unreadable
   */
  #readPrices() {
    const payload = this.querySelector('[data-cash-prices]');
    if (!payload) return {};

    try {
      return JSON.parse(payload.textContent || '{}');
    } catch (error) {
      console.warn('[rewards] unreadable PDP variant price map:', error);
      return {};
    }
  }
}

if (!customElements.get('includ-pdp-cash')) {
  customElements.define('includ-pdp-cash', IncludPdpCash);
}
